import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetProfilesByIdQueryKey,
  useDeleteProfilesById,
} from '@/api/control/control-plane/control-plane';
import { useRoles } from '@/contexts/RolesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog';
import { Trash2 } from 'lucide-react';
import { useError } from '@/contexts/ErrorContext';
import { toast } from 'sonner';
import { ProfileDto } from '@/api/control/missionImpactAPI.schemas';

/**
 * Props for the DeleteProfileDialog component
 */
interface DeleteProfileDialogProps {
  /** The organization ID */
  orgId: string;
  /** The profile to delete */
  profile: ProfileDto;
  /** Optional controlled open state */
  open?: boolean;
  /** Optional controlled open change handler */
  onOpenChange?: (open: boolean) => void;
  /** Optional custom trigger element */
  trigger?: React.ReactNode;
}

/**
 * Dialog for deleting a profile with confirmation
 */
export function DeleteProfileDialog({
  orgId,
  profile,
  open: controlledOpen,
  onOpenChange,
  trigger,
}: DeleteProfileDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const { mutate: deleteProfile, isPending: isDeleting } = useDeleteProfilesById({
    mutation: {
      onSuccess: () => {
        setIsOpen(false);
        toast.success(t.dialogs.deleteProfile.successMessage);
        // Drop the deleted profile from every cached view so no page shows it (or refetches it and
        // gets a "not found") after navigating away.
        queryClient.removeQueries({ queryKey: getGetProfilesByIdQueryKey(orgId, profile.id) });
        queryClient.removeQueries({ queryKey: ['profiles', 'detail', orgId, profile.id] });
        void queryClient.invalidateQueries({ queryKey: ['profiles', 'list', orgId] });
        // Navigate back to profiles list
        void navigate('/dashboard/profiles');
      },
      onError: (error) => {
        handleApiError(error, { context: 'DeleteProfileDialog.deleteProfile', profile });
      },
    },
  });

  /**
   * Handles the delete action
   */
  const handleDelete = () => {
    deleteProfile({ orgId, profileId: profile.id });
  };

  if (!isAdminOrOperator) {
    return null;
  }

  const warningContent = (
    <div className="space-y-3">
      <p className="text-sm">{t.dialogs.deleteProfile.warningIntro}</p>
      <ul className="list-disc list-inside text-sm space-y-1">
        <li>{t.dialogs.deleteProfile.warningSources}</li>
        <li>{t.dialogs.deleteProfile.warningTargets}</li>
        <li>{t.dialogs.deleteProfile.warningSourceTables}</li>
        <li>{t.dialogs.deleteProfile.warningWebhooks}</li>
      </ul>
    </div>
  );

  const defaultTrigger = (
    <Button variant="outline" className="h-9">
      <Trash2 className="mr-2 h-4 w-4 text-destructive" />
      {t.dialogs.deleteProfile.buttonText}
    </Button>
  );

  return (
    <ConfirmDeleteDialog
      trigger={trigger ?? defaultTrigger}
      title={t.dialogs.deleteProfile.title}
      description={
        <>
          {t.dialogs.deleteProfile.description} <strong>{profile.name}</strong>?
        </>
      }
      confirmationKey={profile.name}
      confirmationLabel={t.dialogs.deleteProfile.confirmationLabel}
      warningMessage={warningContent}
      onDelete={handleDelete}
      isDeleting={isDeleting}
      open={isOpen}
      onOpenChange={setIsOpen}
    />
  );
}
